import { isTerminalStatus, type JobView } from '@detachly/contracts'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useApi } from '../lib/api-context'
import { pollIntervalMs } from '../lib/polling'

export type JobPolling = {
  job: JobView | null
  error: unknown
  loading: boolean
  /** False once the job reached a terminal state and polling stopped. */
  polling: boolean
  /** Starts the loop again after a failed poll, without leaving the page. */
  retry: () => void
}

/**
 * Follows one job until it stops moving: every 2s at first, every 5s after two minutes, then not
 * at all. `onSettled` fires once, so a screen can refresh the surrounding data when a job lands.
 */
export function useJobPolling(jobId: string, onSettled?: (job: JobView) => void): JobPolling {
  const api = useApi()
  const [job, setJob] = useState<JobView | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [polling, setPolling] = useState(true)
  const [attempt, setAttempt] = useState(0)

  const settledRef = useRef(false)
  const onSettledRef = useRef(onSettled)
  onSettledRef.current = onSettled
  const shownJobRef = useRef(jobId)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    const startedAt = Date.now()
    settledRef.current = false
    setPolling(true)

    // Another job id means the screen is about somebody else's work now.
    if (shownJobRef.current !== jobId) {
      shownJobRef.current = jobId
      setJob(null)
      setError(null)
      setLoading(true)
    }

    const tick = async () => {
      try {
        const next = await api.getJob(jobId)
        if (cancelled) return

        setJob(next)
        setError(null)

        if (isTerminalStatus(next.status)) {
          setPolling(false)
          if (!settledRef.current) {
            settledRef.current = true
            onSettledRef.current?.(next)
          }
          return
        }

        const wait = pollIntervalMs(next.status, Date.now() - startedAt)
        if (wait !== null) timer = setTimeout(tick, wait)
      } catch (cause: unknown) {
        if (cancelled) return
        // A failed poll stops the loop: retrying a 404 or a 401 forever helps nobody. The screen
        // keeps whatever it had and offers `retry`.
        setError(cause)
        setPolling(false)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void tick()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [api, jobId, attempt])

  return {
    job,
    error,
    loading,
    polling,
    retry: useCallback(() => {
      setError(null)
      setAttempt((value) => value + 1)
    }, []),
  }
}
